import { Badge } from "@/components/ui/badge";
import { invoiceState } from "@/lib/invoice";
import type { OrgInvoice, OrgMember, SeatStatus } from "@/types/api";

/**
 * "Invited but never joined" and "joined then went quiet" are different
 * problems calling for different actions from an admin. Collapsing both into
 * "inactive" hides the one they can actually do something about.
 */
export function SeatStatusBadge({ status }: { status: SeatStatus }) {
	const map: Record<
		SeatStatus,
		{ label: string; variant: "active" | "invited" | "inactive" }
	> = {
		active: { label: "Active", variant: "active" },
		invited: { label: "Invited", variant: "invited" },
		deactivated: { label: "Removed", variant: "inactive" },
	};
	const { label, variant } = map[status];
	return <Badge variant={variant}>{label}</Badge>;
}

export function RoleBadge({ role }: { role: OrgMember["role"] }) {
	if (role === "member") return null;
	return (
		<Badge variant="neutral">{role === "owner" ? "Owner" : "Admin"}</Badge>
	);
}

/**
 * An unpaid invoice past its due date is the one state an admin must act on,
 * so it reads as critical rather than sharing "issued" with everything else
 * that is merely waiting. The decision itself lives in lib/invoice, where it
 * can be tested without rendering anything.
 */
export function InvoiceStatusBadge({
	status,
	dueAt,
}: {
	status: OrgInvoice["status"];
	dueAt: string | null;
}) {
	switch (invoiceState({ status, due_at: dueAt })) {
		case "paid":
			return <Badge variant="active">Paid</Badge>;
		case "cancelled":
			return <Badge variant="inactive">Cancelled</Badge>;
		case "expired":
			return <Badge variant="critical">Expired</Badge>;
		case "overdue":
			return <Badge variant="critical">Overdue</Badge>;
		default:
			return <Badge variant="invited">Due</Badge>;
	}
}
